import { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Badge,
  Dropdown,
  Option,
  Textarea,
  Field,
  Divider,
  Tooltip,
} from '@fluentui/react-components';
import {
  DismissRegular,
  PersonRegular,
  MailRegular,
  PhoneRegular,
  SendRegular,
  ClockRegular,
  CheckmarkCircleRegular,
  ChatRegular,
} from '@fluentui/react-icons';
import { format, formatDistanceToNow } from 'date-fns';
import {
  updateInquiry,
  replyToInquiry,
  markInquiryMessageRead,
} from '../services/api';
import {
  type ClientInquiry,
  type InquiryStatus,
  type InquiryPriority,
  type InquiryCategory,
  type InquiryMessage,
  type SystemUser,
  inquiryStatusDisplayNames,
  inquiryStatusColors,
  inquiryPriorityDisplayNames,
  inquiryPriorityColors,
  inquiryCategoryOptions,
} from '../types';
import { getDataverseRecordUrl, isDataverseConfigured } from '../utils/dataverse';

interface InquiryDetailPanelProps {
  inquiry: ClientInquiry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  systemUsers?: SystemUser[];
  onUpdate?: () => void;
}

const statusOptions: InquiryStatus[] = ['new', 'in_progress', 'waiting_for_client', 'resolved', 'closed'];
const priorityOptions: InquiryPriority[] = ['urgent', 'high', 'normal', 'low'];

export default function InquiryDetailPanel({
  inquiry,
  open,
  onOpenChange,
  systemUsers,
  onUpdate,
}: InquiryDetailPanelProps) {
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [replyText, setReplyText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<InquiryStatus>(inquiry.status);
  const [selectedPriority, setSelectedPriority] = useState<InquiryPriority>(inquiry.priority);
  const [selectedCategory, setSelectedCategory] = useState<InquiryCategory | undefined>(inquiry.category);
  const [selectedAssignee, setSelectedAssignee] = useState<string | undefined>(inquiry.assignedTo);

  // Reset state when inquiry changes
  useEffect(() => {
    setSelectedStatus(inquiry.status);
    setSelectedPriority(inquiry.priority);
    setSelectedCategory(inquiry.category);
    setSelectedAssignee(inquiry.assignedTo);
  }, [inquiry]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [inquiry.messages]);

  // Mark unread messages as read when viewing
  useEffect(() => {
    if (open) {
      const unreadMessages = inquiry.messages.filter(m => m.direction === 'inbound' && !m.readAt);
      unreadMessages.forEach(msg => {
        markInquiryMessageRead(inquiry.id, msg.messageId, inquiry.clientId).catch(console.error);
      });
    }
  }, [open, inquiry]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<{ status: InquiryStatus; priority: InquiryPriority; category: InquiryCategory; assignedTo: string; assignedToName: string }>) =>
      updateInquiry(inquiry.id, { clientId: inquiry.clientId, ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['inquiryStats'] });
      onUpdate?.();
    },
  });

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: (body: string) =>
      replyToInquiry(inquiry.id, { clientId: inquiry.clientId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['inquiryStats'] });
      setReplyText('');
      onUpdate?.();
    },
  });

  const handleStatusChange = (status: InquiryStatus) => {
    setSelectedStatus(status);
    updateMutation.mutate({ status });
  };

  const handlePriorityChange = (priority: InquiryPriority) => {
    setSelectedPriority(priority);
    updateMutation.mutate({ priority });
  };

  const handleCategoryChange = (category: InquiryCategory) => {
    setSelectedCategory(category);
    updateMutation.mutate({ category });
  };

  const handleAssigneeChange = (assignedTo: string) => {
    const user = systemUsers?.find(u => u.systemUserId === assignedTo);
    setSelectedAssignee(assignedTo);
    updateMutation.mutate({
      assignedTo,
      assignedToName: user?.fullName,
    });
  };

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    replyMutation.mutate(replyText);
  };

  const renderMessage = (message: InquiryMessage) => {
    const isInbound = message.direction === 'inbound';
    return (
      <div
        key={message.messageId}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isInbound ? 'flex-start' : 'flex-end',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            maxWidth: '80%',
            padding: '12px 16px',
            borderRadius: '12px',
            backgroundColor: isInbound ? 'var(--tl-bg-subtle)' : '#0078d4',
            color: isInbound ? 'var(--tl-text-primary)' : 'white',
          }}
        >
          <div style={{ fontSize: '12px', marginBottom: '4px', opacity: 0.8 }}>
            {message.fromName || message.fromEmail}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {message.isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.body, {
                ALLOWED_TAGS: ['div', 'span', 'p', 'h1', 'h2', 'h3', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'a', 'table', 'tr', 'td', 'th', 'tbody', 'thead'],
                ALLOWED_ATTR: ['style', 'href', 'class']
              }) }} />
            ) : (
              message.body
            )}
          </div>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--tl-text-tertiary)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {format(new Date(message.receivedAt), 'MMM d, h:mm a')}
          {message.readAt && isInbound && (
            <Tooltip content={`Read ${format(new Date(message.readAt), 'MMM d, h:mm a')}`} relationship="label">
              <CheckmarkCircleRegular style={{ fontSize: '12px', color: '#10B981' }} />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={() => onOpenChange(false)}
            />
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{inquiry.subject}</span>
            <Badge
              appearance="filled"
              style={{
                backgroundColor: inquiryPriorityColors[inquiry.priority],
                color: 'white',
              }}
              size="small"
            >
              {inquiryPriorityDisplayNames[inquiry.priority]}
            </Badge>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {/* Client Info */}
        <div style={{ padding: '16px', backgroundColor: 'var(--tl-bg-page)', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <PersonRegular style={{ fontSize: '20px' }} />
            <div>
              <div style={{ fontWeight: 600 }}>{inquiry.clientName}</div>
              <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MailRegular style={{ fontSize: '12px' }} />
                {inquiry.clientEmail}
              </div>
              {inquiry.clientPhone && (
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PhoneRegular style={{ fontSize: '12px' }} />
                  {inquiry.clientPhone}
                </div>
              )}
            </div>
          </div>
          {isDataverseConfigured() && inquiry.clientId && (
            <Button
              as="a"
              appearance="subtle"
              size="small"
              href={getDataverseRecordUrl('contact', inquiry.clientId) ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in Dataverse
            </Button>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <Field label="Status" size="small">
            <Dropdown
              value={selectedStatus}
              onOptionSelect={(_, data) => handleStatusChange(data.optionValue as InquiryStatus)}
              style={{ width: '100%' }}
            >
              {statusOptions.map((status) => (
                <Option key={status} text={inquiryStatusDisplayNames[status]} value={status}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: inquiryStatusColors[status],
                      }}
                    />
                    {inquiryStatusDisplayNames[status]}
                  </div>
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Priority" size="small">
            <Dropdown
              value={selectedPriority}
              onOptionSelect={(_, data) => handlePriorityChange(data.optionValue as InquiryPriority)}
              style={{ width: '100%' }}
            >
              {priorityOptions.map((priority) => (
                <Option key={priority} text={inquiryPriorityDisplayNames[priority]} value={priority}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: inquiryPriorityColors[priority],
                      }}
                    />
                    {inquiryPriorityDisplayNames[priority]}
                  </div>
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Category" size="small">
            <Dropdown
              value={selectedCategory || ''}
              onOptionSelect={(_, data) => handleCategoryChange(data.optionValue as InquiryCategory)}
              style={{ width: '100%' }}
              placeholder="Select category"
            >
              {inquiryCategoryOptions.map((cat) => (
                <Option key={cat.value} text={cat.label} value={cat.value}>
                  {cat.label}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Assigned To" size="small">
            <Dropdown
              value={selectedAssignee || ''}
              onOptionSelect={(_, data) => handleAssigneeChange(data.optionValue as string)}
              style={{ width: '100%' }}
              placeholder="Unassigned"
            >
              <Option text="Unassigned" value="">Unassigned</Option>
              {systemUsers?.map((user) => (
                <Option key={user.systemUserId} text={user.fullName} value={user.systemUserId}>
                  {user.fullName}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        <Divider />

        {/* Messages Thread */}
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <div style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChatRegular />
            Conversation ({inquiry.messages.length} messages)
          </div>
          <div
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              padding: '8px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid var(--tl-border)',
            }}
          >
            {inquiry.messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Reply Composer */}
        <div style={{ marginTop: '16px' }}>
          <Field label="Reply">
            <Textarea
              value={replyText}
              onChange={(_, data) => setReplyText(data.value)}
              placeholder="Type your reply to the client..."
              rows={4}
              style={{ width: '100%' }}
            />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <Button
              appearance="primary"
              icon={<SendRegular />}
              onClick={handleSendReply}
              disabled={!replyText.trim() || replyMutation.isPending}
            >
              {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
            </Button>
          </div>
        </div>

        {/* Metadata */}
        <Divider style={{ margin: '16px 0' }} />
        <div style={{ fontSize: '12px', color: 'var(--tl-text-tertiary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
            <ClockRegular />
            Created {format(new Date(inquiry.createdAt), 'MMM d, yyyy h:mm a')}
          </div>
          <div>Last activity {formatDistanceToNow(new Date(inquiry.lastMessageAt), { addSuffix: true })}</div>
          {inquiry.resolvedAt && (
            <div>Resolved {format(new Date(inquiry.resolvedAt), 'MMM d, yyyy h:mm a')}</div>
          )}
        </div>
      </DrawerBody>
    </Drawer>
  );
}
